import React, { useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';

const STATUSES = [
  'Planned',
  'Ready',
  'Released',
  'InProgress',
  'OnHold',
  'Completed',
  'Verified',
  'Closed',
];

type Props = { projectId?: string };

export const ExecutionPackagesPanel: React.FC<Props> = ({ projectId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [dailyOpen, setDailyOpen] = useState(false);
  const [dailyPackageId, setDailyPackageId] = useState<string | undefined>();
  const [linksOpen, setLinksOpen] = useState(false);
  const [linksPackageId, setLinksPackageId] = useState<string | undefined>();
  const [form] = Form.useForm();
  const [dailyForm] = Form.useForm();
  const [drawingId, setDrawingId] = useState<string | undefined>();
  const [dependsOnId, setDependsOnId] = useState<string | undefined>();

  const packagesQ = useQuery({
    queryKey: ['cep-packages', projectId],
    queryFn: () => epcService.getExecutionPackages(projectId),
    enabled: !!projectId,
    staleTime: 15_000,
  });

  const areasQ = useQuery({
    queryKey: ['cep-areas', projectId],
    queryFn: () => epcService.getWorkAreas(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const drawingsQ = useQuery({
    queryKey: ['epc-drawings', projectId],
    queryFn: () => epcService.getDrawings(projectId),
    enabled: !!projectId && linksOpen,
    staleTime: 30_000,
  });

  const detailQ = useQuery({
    queryKey: ['cep-package-detail', linksPackageId],
    queryFn: () => epcService.getExecutionPackage(linksPackageId!),
    enabled: !!linksPackageId && linksOpen,
    staleTime: 5_000,
  });

  const createMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.createExecutionPackage({ ...values, projectId }),
    onSuccess: () => {
      message.success('Execution package created');
      setOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['cep-packages'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      epcService.patchExecutionPackage(id, { status }),
    onSuccess: () => {
      message.success('Status updated');
      void qc.invalidateQueries({ queryKey: ['cep-packages'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const dailyMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.createPackageDailyLog(dailyPackageId!, values),
    onSuccess: () => {
      message.success('Daily execution logged on package');
      setDailyOpen(false);
      dailyForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['cep-packages'] });
      void qc.invalidateQueries({ queryKey: ['cep-progress'] });
      void qc.invalidateQueries({ queryKey: ['cep-work-fronts'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const invalidateLinks = () => {
    void qc.invalidateQueries({ queryKey: ['cep-package-detail', linksPackageId] });
    void qc.invalidateQueries({ queryKey: ['cep-packages'] });
    void qc.invalidateQueries({ queryKey: ['cep-work-fronts'] });
  };

  const linkDrawingMut = useMutation({
    mutationFn: () => epcService.linkPackageDrawing(linksPackageId!, { drawingId }),
    onSuccess: () => {
      message.success('Drawing linked');
      setDrawingId(undefined);
      invalidateLinks();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const unlinkDrawingMut = useMutation({
    mutationFn: (drawingIdToRemove: string) =>
      epcService.unlinkPackageDrawing(linksPackageId!, drawingIdToRemove),
    onSuccess: () => {
      message.success('Drawing unlinked');
      invalidateLinks();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const addDepMut = useMutation({
    mutationFn: () => epcService.addPackageDependency(linksPackageId!, dependsOnId!),
    onSuccess: () => {
      message.success('Dependency added');
      setDependsOnId(undefined);
      invalidateLinks();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const removeDepMut = useMutation({
    mutationFn: (depId: string) => epcService.removePackageDependency(linksPackageId!, depId),
    onSuccess: () => {
      message.success('Dependency removed');
      invalidateLinks();
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) {
    return <Tag>Select a project to manage Execution Packages</Tag>;
  }

  const rows = (packagesQ.data as any[]) || [];
  const areas = (areasQ.data as any[]) || [];
  const drawings = (drawingsQ.data as any[]) || [];
  const detail = (detailQ.data || {}) as any;
  const linkedDrawings = (detail.drawings as any[]) || [];
  const linkedDeps = (detail.dependencies as any[]) || [];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" onClick={() => setOpen(true)}>
          New Execution Package
        </Button>
        <Tag color="geekblue">CEP packages</Tag>
        <Tag>Link drawings/deps to drive work-front readiness</Tag>
      </Space>
      <Table
        size="small"
        rowKey={(r) => String(r.id)}
        loading={packagesQ.isLoading}
        dataSource={rows}
        columns={[
          { title: 'Number', dataIndex: 'package_number', width: 120 },
          { title: 'Description', dataIndex: 'description', ellipsis: true },
          { title: 'Discipline', dataIndex: 'discipline_code', width: 100 },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 140,
            render: (v: string, r: any) => (
              <Select
                size="small"
                style={{ width: 130 }}
                value={v}
                options={STATUSES.map((s) => ({ value: s, label: s }))}
                onChange={(status) => patchMut.mutate({ id: String(r.id), status })}
              />
            ),
          },
          {
            title: 'Drawings',
            dataIndex: 'drawing_count',
            width: 80,
            render: (v: unknown) => String(v ?? 0),
          },
          {
            title: 'Deps',
            dataIndex: 'dependency_count',
            width: 70,
            render: (v: unknown, r: any) =>
              Number(r.open_dependency_count || 0) > 0 ? (
                <Tag color="orange">{String(v ?? 0)} open</Tag>
              ) : (
                String(v ?? 0)
              ),
          },
          {
            title: 'Progress %',
            dataIndex: 'progress_pct',
            width: 90,
            render: (v: unknown) => String(v ?? 0),
          },
          {
            title: '',
            width: 200,
            render: (_: unknown, r: any) => (
              <Space size={4}>
                <Button
                  size="small"
                  onClick={() => {
                    setLinksPackageId(String(r.id));
                    setLinksOpen(true);
                  }}
                >
                  Links
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setDailyPackageId(String(r.id));
                    dailyForm.setFieldsValue({
                      reportDate: new Date().toISOString().slice(0, 10),
                      completedQty: 0,
                      installedQty: 0,
                    });
                    setDailyOpen(true);
                  }}
                >
                  Log today
                </Button>
              </Space>
            ),
          },
        ]}
      />
      <Modal
        title="New Execution Package"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => createMut.mutate(values)}
          initialValues={{ status: 'Planned', priority: 50 }}
        >
          <Form.Item name="packageNumber" label="Package number (optional)">
            <Input placeholder="Auto EP-00001 if blank" />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="disciplineCode" label="Discipline">
            <Input placeholder="MECH / CIVIL / ELEC…" />
          </Form.Item>
          <Form.Item name="constructionAreaId" label="Construction area">
            <Select
              allowClear
              options={areas.map((a: any) => ({
                value: String(a.id),
                label: `${a.code} — ${a.title}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="priority" label="Priority">
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select options={STATUSES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Capture daily execution (on package)"
        open={dailyOpen}
        onCancel={() => setDailyOpen(false)}
        onOk={() => dailyForm.submit()}
        confirmLoading={dailyMut.isPending}
        destroyOnClose
      >
        <Form form={dailyForm} layout="vertical" onFinish={(v) => dailyMut.mutate(v)}>
          <Form.Item name="reportDate" label="Report date" rules={[{ required: true }]}>
            <Input type="date" />
          </Form.Item>
          <Form.Item name="startTime" label="Start time">
            <Input placeholder="08:00" />
          </Form.Item>
          <Form.Item name="finishTime" label="Finish time">
            <Input placeholder="17:00" />
          </Form.Item>
          <Form.Item name="completedQty" label="Completed qty">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="installedQty" label="Installed qty">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="weather" label="Weather">
            <Input />
          </Form.Item>
          <Form.Item name="constraintsText" label="Constraints">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Package drawings & dependencies"
        open={linksOpen}
        onCancel={() => {
          setLinksOpen(false);
          setLinksPackageId(undefined);
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Space style={{ marginBottom: 8 }} wrap>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Link engineering drawing"
                style={{ minWidth: 280 }}
                value={drawingId}
                onChange={setDrawingId}
                options={drawings.map((d: any) => ({
                  value: String(d.id),
                  label: `${d.drawing_number || d.id} — ${d.title || ''}`,
                }))}
              />
              <Button
                type="primary"
                disabled={!drawingId}
                loading={linkDrawingMut.isPending}
                onClick={() => linkDrawingMut.mutate()}
              >
                Link drawing
              </Button>
            </Space>
            <Table
              size="small"
              pagination={false}
              loading={detailQ.isLoading}
              rowKey={(r) => String(r.drawing_id || r.id)}
              dataSource={linkedDrawings}
              locale={{ emptyText: 'No drawings linked — work front may wait on drawings' }}
              columns={[
                {
                  title: 'Drawing',
                  render: (_: unknown, r: any) =>
                    `${r.drawing_number || r.drawing_id}${r.drawing_title ? ` — ${r.drawing_title}` : ''}`,
                },
                { title: 'Rev', dataIndex: 'current_rev', width: 80 },
                {
                  title: '',
                  width: 90,
                  render: (_: unknown, r: any) => (
                    <Button
                      size="small"
                      danger
                      loading={unlinkDrawingMut.isPending}
                      onClick={() => unlinkDrawingMut.mutate(String(r.drawing_id))}
                    >
                      Unlink
                    </Button>
                  ),
                },
              ]}
            />
          </div>
          <div>
            <Space style={{ marginBottom: 8 }} wrap>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Depends on package"
                style={{ minWidth: 220 }}
                value={dependsOnId}
                onChange={setDependsOnId}
                options={rows
                  .filter((p: any) => String(p.id) !== String(linksPackageId))
                  .map((p: any) => ({
                    value: String(p.id),
                    label: `${p.package_number} (${p.status})`,
                  }))}
              />
              <Button
                type="primary"
                disabled={!dependsOnId}
                loading={addDepMut.isPending}
                onClick={() => addDepMut.mutate()}
              >
                Add dependency
              </Button>
            </Space>
            <Table
              size="small"
              pagination={false}
              loading={detailQ.isLoading}
              rowKey={(r) => String(r.depends_on_package_id || r.id)}
              dataSource={linkedDeps}
              locale={{ emptyText: 'No predecessor packages' }}
              columns={[
                {
                  title: 'Predecessor',
                  render: (_: unknown, r: any) =>
                    r.depends_on_package_number || r.depends_on_package_id,
                },
                {
                  title: 'Status',
                  dataIndex: 'depends_on_status',
                  width: 110,
                  render: (v: string) => (
                    <Tag
                      color={
                        ['Completed', 'Verified', 'Closed'].includes(v) ? 'green' : 'orange'
                      }
                    >
                      {v || '—'}
                    </Tag>
                  ),
                },
                {
                  title: '',
                  width: 90,
                  render: (_: unknown, r: any) => (
                    <Button
                      size="small"
                      danger
                      loading={removeDepMut.isPending}
                      onClick={() =>
                        removeDepMut.mutate(String(r.depends_on_package_id))
                      }
                    >
                      Remove
                    </Button>
                  ),
                },
              ]}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default ExecutionPackagesPanel;
